// ── Contract Requirements checklist (layout-2 §4, 2026-09-23) ────────────────
// One row per requirement the contract may impose. The section owner answers
// every row — Applies or Doesn't apply (never left blank; an unreviewed row
// is not "doesn't apply"). An applicable row then needs its details and, if it
// names a docType, an uploaded §4 document of that type.
// Stored per turnover in TurnoverContractRequirements (db/04).
//
// Adding a row = one entry here (+ its docType in turnoverLayout.js's
// "requirements" list if new). Keys are stored — never rename one.

export const RESPONSIBILITY_OPTIONS = ["CleanSpace", "GC / Contract Holder", "Owner", "Subcontractor", "Other"];

export const REQUIREMENT_GROUPS = [
  { key: "tax_admin", label: "Tax & administrative" },
  { key: "labor", label: "Labor" },
  { key: "safety", label: "Safety" },
  { key: "bonds_permits", label: "Bonds & permits" },
  { key: "commercial", label: "Commercial terms" },
];

// fields: extra inputs shown when the row applies. required: must be filled.
export const CONTRACT_REQUIREMENTS = [
  { key: "tax_exempt", group: "tax_admin", label: "Tax-exempt status",
    help: "Is the work (or the client) sales-tax exempt?", docType: "Tax Exempt Certificate",
    fields: [{ key: "certificate_number", label: "Certificate #" }, { key: "expiration", label: "Expiration", type: "date" }] },
  { key: "w9", group: "tax_admin", label: "W-9", help: "Does the client require a W-9 from CleanSpace?", docType: "W-9" },
  { key: "coi", group: "tax_admin", label: "Certificate of insurance", help: "Is a COI required (limits, additional insureds)?", docType: "Insurance Certificate",
    fields: [{ key: "notes", label: "Limits / additional insureds", type: "textarea" }] },
  { key: "lien_waivers", group: "tax_admin", label: "Lien waivers", help: "Are lien waivers required with pay applications?", docType: "Lien Waiver",
    fields: [{ key: "notes", label: "Form / frequency (conditional, unconditional, …)", type: "textarea" }] },

  { key: "prevailing_wage", group: "labor", label: "Prevailing wage", help: "Does prevailing wage apply?", docType: "Prevailing Wage Determination", docOptional: true },
  { key: "certified_payroll", group: "labor", label: "Certified payroll / Davis-Bacon", help: "Is certified payroll or Davis-Bacon reporting required?", docType: "Davis-Bacon Certification" },

  { key: "site_safety_plan", group: "safety", label: "Site safety plan", help: "Is a site-specific safety plan required?", docType: "Site Safety Plan" },
  { key: "owner_safety", group: "safety", label: "Owner safety program", help: "Does the owner impose its own safety program?", docType: "Owner Safety Program" },
  { key: "ocip_ccip", group: "safety", label: "OCIP / CCIP", help: "Is the project under an owner- or contractor-controlled insurance program?", docType: "OCIP / CCIP Enrollment" },

  { key: "bonds", group: "bonds_permits", label: "Performance & payment bonds", help: "Are performance or payment bonds required?", docType: "Bonding Document",
    responsibility: true, fields: [{ key: "amount", label: "Bond amount / %" }] },
  { key: "permits", group: "bonds_permits", label: "Permits", help: "Are permits required? Who pulls and maintains them?",
    responsibility: true, fields: [{ key: "notes", label: "Which permits", type: "textarea" }] },
  { key: "ahj", group: "bonds_permits", label: "AHJ requirements", help: "Are there authority-having-jurisdiction requirements beyond standard permitting?",
    fields: [{ key: "notes", label: "What the AHJ requires", type: "textarea", required: true }] },

  { key: "liquidated_damages", group: "commercial", label: "Liquidated damages", help: "Does the contract include LD provisions?",
    fields: [{ key: "terms", label: "LD terms (rate, cap, trigger)", type: "textarea", required: true }] },
];

function parseDetails(row) {
  if (!row?.DetailsJson) return {};
  if (typeof row.DetailsJson === "object") return row.DetailsJson;
  try { return JSON.parse(row.DetailsJson) || {}; } catch { return {}; }
}

export function requirementRowsByKey(rows = []) {
  const map = new Map();
  for (const r of rows || []) map.set(r.RequirementKey, { ...r, details: parseDetails(r) });
  return map;
}

const answered = (row) => row && (row.Applies === true || row.Applies === false || row.Applies === 1 || row.Applies === 0);
const applies = (row) => row && (row.Applies === true || row.Applies === 1);

// Status of every row and what still blocks the section.
// sectionDocs: this turnover's §4 documents.
export function evaluateRequirements(rows = [], sectionDocs = []) {
  const byRow = requirementRowsByKey(rows);
  const docTypes = new Set((sectionDocs || []).map(d => d.DocType).filter(Boolean));
  const items = CONTRACT_REQUIREMENTS.map(def => {
    const row = byRow.get(def.key) || null;
    const needs = [];
    let status;
    if (!answered(row)) { status = "unanswered"; needs.push("Answer Applies or Doesn't apply"); }
    else if (!applies(row)) status = "not_applicable";
    else {
      if (def.responsibility && !row.Responsibility) needs.push("Choose who is responsible");
      for (const f of def.fields || []) {
        if (f.required && !String(row.details?.[f.key] ?? "").trim()) needs.push(`Enter ${f.label.toLowerCase()}`);
      }
      // docOptional: the document is welcome but never blocks (prevailing wage — 2026-09-23).
      if (def.docType && !def.docOptional && !docTypes.has(def.docType)) needs.push(`Upload the ${def.docType}`);
      status = needs.length ? "incomplete" : "complete";
    }
    return { def, row, status, needs, hasDocument: def.docType ? docTypes.has(def.docType) : null };
  });
  const missing = items.flatMap(i => i.needs.map(n => (i.status === "unanswered" ? `${i.def.label}: applies?` : `${i.def.label}: ${n}`)));
  return {
    items, missing,
    answeredCount: items.filter(i => i.status !== "unanswered").length,
    total: items.length,
    applicableCount: items.filter(i => i.status === "complete" || i.status === "incomplete").length,
  };
}

// What the estimate's Financial Compliance record says, as a hint only — the
// owner still answers each row (TurnoverFinancialCompliance, db/27).
export function estimateHint(key, fc) {
  if (!fc) return null;
  const yes = (v) => v === true || v === 1 || v === "true";
  switch (key) {
    case "prevailing_wage":   return fc.PrevailingWage != null ? `Estimate: ${yes(fc.PrevailingWage) ? "yes" : "no"}` : null;
    case "certified_payroll": return fc.CertifiedPayrollRequired != null ? `Estimate: ${yes(fc.CertifiedPayrollRequired) ? "yes" : "no"}` : null;
    case "tax_exempt":        return fc.TaxStatus ? `Estimate tax status: ${fc.TaxStatus}` : null;
    case "bonds":             return fc.BondsStatus ? `Estimate bonds: ${fc.BondsStatus}` : null;
    case "lien_waivers":      return fc.LienWaiversStatus ? `Estimate lien waivers: ${fc.LienWaiversStatus}` : null;
    case "w9":                return fc.W9Status ? `W-9: ${fc.W9Status}` : null;
    case "coi":               return fc.COIStatus ? `COI: ${fc.COIStatus}` : null;
    default: return null;
  }
}

export function requirementPayload(turnoverId, key, patch, user) {
  const p = { turnover_id: turnoverId, requirement_key: key, by: user?.name || "" };
  if ("applies" in patch) p.applies = patch.applies === null ? null : (patch.applies ? 1 : 0);
  if ("responsibility" in patch) p.responsibility = patch.responsibility || "";
  if ("details" in patch) p.details = patch.details || {};
  return p;
}
